import React, {useCallback, useEffect, useState} from "react";
import {Card, ListGroup} from "react-bootstrap";
import {Location, NewsItem, SettingsDoc, User, Visit} from "../../types/Types";
import {collection, doc, getDoc, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db, functions} from "../../../firebase/FirebaseProvider";
import LocationNav from "./LocationNav";
import LocationDetails from "./LocationDetails";
import LocationDetailsV2 from "./LocationDetailsV2";
import CommentRow from "./CommentRow";
import {httpsCallable} from "@firebase/functions";
import {NOTIFICATION_LIMIT, sanitizeHtmlString, logDataLoaded} from "../../util/Util";
import LocationCheckIn from "./LocationCheckIn";
import PageTracking from "../../auth/PageTracking";
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faPlus} from "@fortawesome/free-solid-svg-icons";
import V2CommentRow from "../../shared/v2/V2CommentRow";

interface Props {
    currentUser: User | undefined
    usersMap: Map<string, User>
    settings: SettingsDoc
    likesMap: Map<string, boolean>
}

function LocationComments(props: Props) {
    const { id } = useParams();
    const [searchParams] = useSearchParams();

    const [location, setLocation] = useState<Location | undefined>(undefined);
    const [visited, setVisited] = useState<boolean>(false);
    const [comment, setComment] = useState<string | undefined>();
    const [commentSending, setCommentSending] = useState<boolean>(false);
    const [commentsData, setComments] = useState<Array<NewsItem> | undefined>(undefined);
    const [feedback, setFeedback] = useState<string>();
    const [isAddCommentOpen, setIsAddCommentOpen] = useState<boolean>(false);

    const updateCommentLikeCount = useCallback((commentId: string, adjustment: number) => {
        setComments(prevComments => {
            if (!prevComments) return prevComments;
            return prevComments.map(comment => {
                if (comment.id === commentId) {
                    return {
                        ...comment,
                        likeCount: (comment.likeCount || 0) + adjustment
                    };
                }
                return comment;
            });
        });
    }, []);

    useEffect(() => {
        if (!id) return;
        
        (async () => {
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                let locationToSet = docSnap.data() as Location;
                locationToSet.id = docSnap.id;
                setLocation(locationToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!id || !props.currentUser?.id) return;
        
        (async () => {
            const visitQuery = query(
                collection(db, 'visits'),
                where('locationId', '==', id),
                where('userId', '==', props.currentUser!.id)
            );
            const visitSnap = await getDocs(visitQuery);
            if (!visitSnap.empty) {
                const visitData = visitSnap.docs[0].data() as Visit;
                setVisited(visitData.visited || false);
            }
        })();
    }, [id, props.currentUser?.id]);

    const toggleVisited = useCallback(() => {
        if (!id) return;
        
        const toggleVisitedFunction = httpsCallable(functions, 'toggleVisitedV2');
        toggleVisitedFunction({locationId: id}).then(async (response) => {
            const result = response.data as { status: number; code: string };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            
            // Reload location and visit status
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                let locationToSet = docSnap.data() as Location;
                locationToSet.id = docSnap.id;
                setLocation(locationToSet);
            }
            
            setVisited(prev => !prev);
        }).catch((error) => {
            console.error('Error toggling visited:', error);
            alert('Failed to update visit status');
        });
    }, [id]);

    const loadComments = useCallback(async() => {
        if(!location?.id) {
            return;
        }

        // Load comments
        const newComments: Array<NewsItem> = [];
        const commentsOnceCollection = query(collection(db, 'comments'),
            where('locationId', '==', location.id),
            where('hidden', '==', false),
            orderBy('lastModified', 'desc'),
            limit(NOTIFICATION_LIMIT));
        const commentsOnceCollectionSnapshot = await getDocs(commentsOnceCollection);
        commentsOnceCollectionSnapshot.forEach((doc) => {
            let tempComment = doc.data() as NewsItem;
            tempComment.id = doc.id;
            newComments.push(tempComment);
        });
        logDataLoaded('location comments', newComments.length);
        setComments(newComments);
    }, [location?.id]);

    useEffect(() => {
        (async () => {
            await loadComments();
        })();
    }, [loadComments]);

    const saveComment = () => {
        if(!location || !comment) {
            return;
        }

        const sanitizedString = sanitizeHtmlString(comment.trim());
        if(!sanitizedString) {
            return;
        }

        setCommentSending(true);

        var saveCommentApi = httpsCallable(functions, 'saveComment');
        saveCommentApi({comment: {
                locationId: location.id,
                locationName: location.name,
                payload: sanitizedString,
                lastModified: new Date()
            }}).then(async r => {
                setFeedback("Successfully saved comment.")
                setComment("");
                setIsAddCommentOpen(false);
                setCommentSending(false);
                await loadComments();
        });
    };

    const hideComment = async (commentId: string) => {
        const hideCommentApi = httpsCallable(functions, 'hideComment');
        await hideCommentApi(
            {
                commentId
            }
        )
        await loadComments();
    };

    const isUiV2 = !!props.currentUser?.enableUiV2;
    const isFocusedCommentsView = isUiV2 && searchParams.get("view") === "comments";
    const visibleComments = commentsData?.filter((newsItem) => {
        const user = props.usersMap.get(newsItem.userId);
        return user && !user.socialDisabled
    });

    if (isFocusedCommentsView) {
        const currentUser = props.currentUser!;
        return (
            <>
                <PageTracking />
                <LocationDetailsV2
                    location={location}
                    currentUser={currentUser}
                    likesMap={props.likesMap}
                    visited={visited}
                    onToggleVisited={toggleVisited}
                    variant="header"
                />
                {location && <V2GlassPanel className="location-v2-detail-toolbar">
                    <Link className="location-v2-back-button" to={`/location/${location.id}/comments`}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Pub
                    </Link>
                    <div className="location-v2-detail-stats">
                        <V2GlassSurface className="location-v2-detail-stat">
                            <span>Comments</span>
                            <b>{visibleComments?.length || 0}</b>
                        </V2GlassSurface>
                        {!currentUser.socialDisabled && (
                            <button className={`location-v2-back-button${isAddCommentOpen ? " location-v2-toolbar-button-active" : ""}`} type="button" onClick={() => setIsAddCommentOpen((isOpen) => !isOpen)}>
                                <FontAwesomeIcon icon={faPlus} />
                                Comment
                            </button>
                        )}
                    </div>
                </V2GlassPanel>}
                {location && !currentUser.socialDisabled && isAddCommentOpen && <V2GlassPanel className="location-v2-comment-composer">
                    <textarea
                        className="location-v2-comment-input"
                        placeholder="Type your comment here"
                        value={comment}
                        id="add-comment"
                        onChange={e => {setComment(e.target.value); setFeedback("")}}
                    />
                    <div className="location-v2-comment-composer-footer">
                        <span>{feedback}</span>
                        <button
                            disabled={commentSending || !comment?.trim()}
                            onClick={() => saveComment()}
                            className="location-v2-tag-button"
                            type="button"
                        >
                            {commentSending ? "Saving..." : "Save"}
                        </button>
                    </div>
                </V2GlassPanel>}
                <V2GlassPanel className="location-v2-detail-list-card">
                    {!commentsData && <div className="location-v2-empty-list">Loading...</div>}
                    {visibleComments && visibleComments.length === 0 && <div className="location-v2-empty-list">No comments yet</div>}
                    {visibleComments && visibleComments.map((commentItem: NewsItem) => (
                        <V2CommentRow
                            key={commentItem.id}
                            comment={commentItem}
                            user={props.usersMap.get(commentItem.userId)}
                            session={undefined}
                            currentUser={currentUser}
                            likesMap={props.likesMap}
                            onLikeCountChange={updateCommentLikeCount}
                            canDelete={currentUser.id === commentItem.userId || !!currentUser.admin}
                            onDelete={hideComment}
                        />
                    ))}
                </V2GlassPanel>
            </>
        );
    }

    return (
        <>
            <PageTracking />
            {props.currentUser && (props.currentUser.enableUiV2 ? <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="header"
            /> : <LocationDetails
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
            />)}
            {location && props.currentUser && !props.currentUser.enableUiV2 && !props.currentUser.socialDisabled && <LocationCheckIn currentUser={props.currentUser} location={location}/>}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="location"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="tags"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="widgets"
            />}
            {location && !isUiV2 && <LocationNav settings={props.settings} currentUser={props.currentUser} locationId={location.id}/>}
            {location && !isUiV2 && props.currentUser && !props.currentUser.socialDisabled && <Card className={"mb-2"}>
                <div style={{padding: "0.8em"}}>
                    <div className="form-group p-0">
                        <div>
                            <textarea placeholder={"Type your comment here"} className="form-control tag-input" value={comment} id="add-comment" onChange={e => {setComment(e.target.value); setFeedback("")}}></textarea>
                        </div>
                        <div>
                            <div style={{float: "left", marginTop: "0.6em"}}>
                                <span>{feedback}</span>
                            </div>
                            <div className={""} style={{float: "right", marginTop: "0.6em"}}>
                                <button disabled={commentSending} onClick={() => saveComment()} className={"btn btn-primary btn-sm"}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>}
            {!isUiV2 && <Card className={"mb-3"}>
                <ListGroup variant="flush">
                    {!commentsData && <div className={'loading'}>
                        Loading...
                    </div>}
                    {(commentsData && commentsData.length === 0) && <div className={'loading'}>
                        No comments yet
                    </div>}
                    {commentsData && commentsData
                        .filter((newsItem) => {
                            const user = props.usersMap.get(newsItem.userId);
                            return user && !user.socialDisabled
                        })
                        .map((comment: NewsItem, index: number) => (
                        props.currentUser && <CommentRow 
                            key={comment.id} 
                            user={props.currentUser} 
                            comment={comment} 
                            deleteComment={() => hideComment(comment.id)} 
                            commentUser={props.usersMap.get(comment.userId)}
                            likesMap={props.likesMap}
                            onLikeCountChange={updateCommentLikeCount}
                        />
                    ))}
                </ListGroup>
            </Card>}
        </>
    )
}

export default LocationComments;
